import { Box, Card, CardActionArea, Stack, Typography } from '@mui/material';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import { brandingFixture } from '../lib/branding/branding-fixture';
import { getAdSenseConfig } from '../lib/adsense/adsense-config';
import { AdSenseUnit } from '../lib/adsense/AdSenseUnit';

interface AdSlotProps {
  /** Optional 1-indexed slot position for diagnostics / analytics. */
  readonly slot?: number;
  /**
   * Visual variant. `default` renders an inline outlined card (used
   * between sections); `release` renders a full-bleed hero card the
   * same shape as a `<ReleaseCard>` so an ad slot can sit inline
   * within the release grid per the Daily-page layout. AdSense format
   * mapping below.
   */
  readonly variant?: 'default' | 'release';
}

/**
 * AdSense format mapping per Google's "Ad placement policies" guidance
 * (https://support.google.com/adsense/answer/1346295?hl=en):
 *
 *   - `release` (in-grid, image-shape, 16:9) → "Display ads — fixed,
 *     responsive square / rectangle". Renders inside the release grid
 *     where the surrounding tiles are also 16:9 image cards, so the ad
 *     visually matches the content density. AdSense responsive display
 *     ads scale to the parent box; the 16:9 aspect-ratio constraint
 *     keeps the inventory consistent for the auction.
 *   - `default` (between-sections, full-width banner) → "Display ads —
 *     fixed, responsive horizontal banner". Sits between content
 *     sections with clear separation from the editorial content above
 *     and below per the policy on "ad placements that may cause
 *     accidental clicks" (the ad does not crowd the section heading
 *     or the first content item).
 *
 * Both placeholder slots also carry a "Sponsored" / "Advertisement"
 * label so the slot is clearly distinguishable from editorial
 * content per AdSense's "site behavior — content separation" policy.
 *
 * The placeholders ship with a podcast self-promotion until a real
 * AdSense unit is wired by the operator. The DOM nodes carry
 * `data-ad-slot` so the eventual swap to `<ins class="adsbygoogle">`
 * can target them by attribute selector without changing the
 * parent layout.
 */

/** Hero-style ad slot — same 16:9 shape + hover behavior as `<ReleaseCard>`. */
function ReleaseAdSlot({ slot }: { readonly slot?: number }): JSX.Element {
  return (
    <Card
      elevation={3}
      data-ad-slot={slot}
      data-ad-format="display-rectangle-16x9"
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        overflow: 'hidden',
        borderRadius: 2,
        color: 'common.white',
        background:
          'linear-gradient(135deg, rgba(0,80,80,0.95) 0%, rgba(74,99,99,0.95) 100%)',
        transition: 'transform 200ms ease, box-shadow 200ms ease, filter 200ms ease',
        '&:hover, &:focus-within': {
          transform: 'translateY(-2px)',
          boxShadow: 8,
          filter: 'brightness(1.05)',
        },
      }}
    >
      <CardActionArea
        href="/podcast"
        aria-label="Subscribe to the DVGUP podcast"
        sx={{ height: '100%' }}
      >
        <Stack
          spacing={1}
          sx={{
            height: '100%',
            padding: 2.5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="overline" sx={{ opacity: 0.85 }}>
            Sponsored
          </Typography>
          <Stack spacing={0.5} sx={{ flexGrow: 1, justifyContent: 'center' }}>
            <Typography
              variant="h3"
              component="h3"
              sx={{
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                lineHeight: 1.15,
                fontWeight: 700,
              }}
            >
              {brandingFixture.copy.podcastSubtitle}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '0.95rem', sm: '1rem' },
                lineHeight: 1.4,
                opacity: 0.96,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
              }}
            >
              The DVGUP podcast — releases, news, and a fact you didn&apos;t know.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <PodcastsIcon fontSize="small" />
            <Typography variant="button">Subscribe now</Typography>
          </Stack>
        </Stack>
      </CardActionArea>
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          right: -32,
          bottom: -32,
          width: 240,
          height: 240,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%)',
          pointerEvents: 'none',
        }}
      />
    </Card>
  );
}

/**
 * Inline horizontal-banner ad slot (default variant, used between
 * sections). Maps to the AdSense "Display ads — responsive
 * horizontal banner" format. Adds a clear `Advertisement` label so
 * the placement is visually separated from the editorial content
 * above and below per AdSense's content-separation guidance.
 */
function DefaultAdSlot({ slot }: { readonly slot?: number }): JSX.Element {
  return (
    <Card
      variant="outlined"
      data-ad-slot={slot}
      data-ad-format="display-horizontal-banner"
      sx={{
        my: 2,
        transition: 'transform 200ms ease, box-shadow 200ms ease',
        '&:hover, &:focus-within': {
          transform: 'translateY(-1px)',
          boxShadow: 4,
        },
      }}
    >
      <CardActionArea href="/podcast" aria-label="Subscribe to the DVGUP podcast">
        <Stack spacing={0.5} sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Advertisement
          </Typography>
          <Typography variant="body1">{brandingFixture.copy.podcastSubtitle}</Typography>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

/**
 * `<AdSlot>` — ad container per Requirement 9.1.
 *
 * **AdSense integration.** When the operator has configured the
 * publisher client ID + per-slot IDs (per
 * `lib/adsense/adsense-config.ts`), the slot renders a real
 * `<ins class="adsbygoogle">` AdSense unit. Until then, each slot
 * renders the podcast self-promotion placeholder (which still
 * carries the AdSense-policy-aware "Sponsored" / "Advertisement"
 * label so the placement is clearly distinguishable from editorial
 * content per Google's content-separation policy).
 *
 * Two variants:
 *   - `default` — outlined inline horizontal banner used between
 *     sections. Real AdSense format: `horizontal` (responsive
 *     banner). Placeholder: outlined card linking to `/podcast`.
 *   - `release` — full-bleed 16:9 hero matching `<ReleaseCard>`'s
 *     shape, so the slot can sit inline within the release grid.
 *     Real AdSense format: `rectangle` (responsive square /
 *     rectangle, aspect ratio 16:9). Placeholder: brand-tinted
 *     hero card linking to `/podcast`.
 */
export function AdSlot({ slot, variant = 'default' }: AdSlotProps): JSX.Element {
  const adsense = getAdSenseConfig();
  if (adsense.enabled) {
    if (variant === 'release' && adsense.slots.releaseGrid) {
      return (
        <Box data-ad-slot={slot} data-ad-format="display-rectangle-16x9" sx={{ width: '100%' }}>
          <AdSenseUnit
            slotId={adsense.slots.releaseGrid}
            format="rectangle"
            aspectRatio="16 / 9"
          />
        </Box>
      );
    }
    if (variant === 'default') {
      const slotId =
        slot === 3 ? adsense.slots.betweenSections2 : adsense.slots.betweenSections1;
      if (slotId) {
        return (
          <Box data-ad-slot={slot} data-ad-format="display-horizontal-banner" sx={{ my: 2 }}>
            <AdSenseUnit slotId={slotId} format="horizontal" />
          </Box>
        );
      }
    }
  }
  return variant === 'release' ? <ReleaseAdSlot slot={slot} /> : <DefaultAdSlot slot={slot} />;
}
